const TAIL_SCAN_BYTES = 65536

export const looksLikePdf = (file: Pick<File, 'name' | 'type'>): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

// Match `/Encrypt` only as a COMPLETE PDF name — the next character must be a
// PDF delimiter or whitespace — so longer names (/Encrypted, /EncryptMetadata,
// /EncryptionInfo) never false-positive. A full structural check (trailer /
// xref-dictionary parsing) is deliberately not done: xref streams are
// Flate-compressed, so validating them means shipping a PDF parser; and the
// cost asymmetry favors the rare false positive (recoverable message, and the
// re-save remedy clears it) over a false negative (recreates T-002942).
const ENCRYPT_NAME = /\/Encrypt(?=[\s/<>[\]()%]|$)/

export const pdfBytesHaveEncryptMarker = (
  bytes: ArrayBuffer | Uint8Array,
): boolean => ENCRYPT_NAME.test(new TextDecoder('latin1').decode(bytes))

// 'unreadable' is a distinct outcome: the browser could not read the picked
// file at all (e.g. it was moved or changed on disk after selection) — that
// must not be reported as "password-protected", and it must not escape as a
// rejected promise from an onClick handler. This function never rejects.
export type PdfUploadValidation = 'ok' | 'password-protected' | 'unreadable'

export const validatePdfUpload = async (
  file: File,
): Promise<PdfUploadValidation> => {
  if (!looksLikePdf(file)) return 'ok'
  try {
    const tail = await file
      .slice(Math.max(0, file.size - TAIL_SCAN_BYTES))
      .arrayBuffer()
    return pdfBytesHaveEncryptMarker(tail) ? 'password-protected' : 'ok'
  } catch {
    return 'unreadable'
  }
}
